import { api } from './client'
import type { components } from '@/lib/api/generated/schema'

export interface Role {
    id: string
    code: string
    name: string
    description?: string
    is_system: boolean
    is_active: boolean
}

export interface UserWithRoles {
    id: string
    username: string
    full_name: string
    phone?: string
    email?: string | null
    otp_enabled: boolean
    is_active: boolean
    created_at: string
    updated_at: string
    roles: Role[]
}

// Người chọn được ở bộ lọc "Người tạo" khi xuất Excel đơn (gồm cả người đã nghỉ
// từng tạo đơn — is_active=false).
export type OrderCreator = components['schemas']['OrderCreator']

export interface DisplayUser {
    id: string
    full_name: string
    username: string
}

/**
 * Tham số trang của `/users/selectable` (M-143): có bất kỳ tham số nào thì
 * server tìm + phân trang; `ids` (cách nhau dấu phẩy) chỉ lấy các người đó —
 * dùng để đọc lại tên chip đã chọn.
 */
export interface ListSelectableUsersParams {
    search?: string
    ids?: string
    page?: number
    per_page?: number
}

export interface CreateUserRequest {
    username: string
    password: string
    full_name: string
    phone?: string
    role_ids: string[]
}

export interface UpdateUserRequest {
    full_name?: string
    phone?: string
    is_active?: boolean
}

export interface SetUserOtpRequest {
    enabled: boolean
    email?: string | null
}

export interface ListUsersParams {
    search?: string
    role_id?: string
    is_active?: string
    page?: number
    per_page?: number
}

export const userApi = {
    list: (params?: ListUsersParams) =>
        api.get<UserWithRoles[]>('/users', {
            params: params as Record<string, string | number | undefined>,
        }),

    getById: (id: string) => api.get<UserWithRoles>(`/users/${id}`),

    setOtp: (id: string, data: SetUserOtpRequest) =>
        api.put<UserWithRoles>(`/users/${id}/otp`, data),

    create: (data: CreateUserRequest) =>
        api.post<UserWithRoles>('/users', data),

    update: (id: string, data: UpdateUserRequest) =>
        api.put<UserWithRoles>(`/users/${id}`, data),

    assignRoles: (id: string, roleIds: string[]) =>
        api.put<{ message: string }>(`/users/${id}/roles`, {
            role_ids: roleIds,
        }),

    changePassword: (
        id: string,
        data: { old_password?: string; new_password: string },
    ) => api.put<{ message: string }>(`/users/${id}/password`, data),

    listRoles: () => api.get<Role[]>('/roles'),

    // Display-capable users for the POS customer-display picker.
    // Needs no users.read, so POS operators can populate the picker.
    listDisplays: () => api.get<DisplayUser[]>('/users/displays'),

    // All active users for people-pickers (e.g. care-log implementers).
    // Needs no users.read, so growers self-serving a care log can pick too.
    listSelectable: () => api.get<DisplayUser[]>('/users/selectable'),

    // Same population, searched + paged on the server (load more on scroll).
    listSelectablePage: (params: ListSelectableUsersParams) =>
        api.get<DisplayUser[]>('/users/selectable', {
            params: params as Record<string, string | number | undefined>,
        }),

    // Active users + users who left but created sales orders, active first.
    // Needs invoices.export (feeds the daily Excel export filter).
    listOrderCreators: () => api.get<OrderCreator[]>('/users/order-creators'),
}
